import { useState } from 'react';
import { Button, Choice, Destructive, Icon, Modal, Reason, Textarea, useToast } from '@efira/ui';
import { DIRECTOR_MESSAGE_SECONDS } from '@efira/protocol';
import type { DirectorCommandName } from '@efira/protocol';

import { useDirectorCommand, useDirectorMessage } from '../../api/queries';
import { useConsoleSession } from '../session';
import { HOTKEYS } from '../hotkeys';

/**
 * Управление ходом игры: пауза, перезапуск раунда, сообщение зрителям,
 * завершение сессии.
 *
 * ПОЧЕМУ ЭТО НЕ КОСМЕТИКА. Без этих кнопок стример в эфире не может ни
 * остановить игру, чтобы ответить зрителю, ни перезапустить раунд, испорченный
 * накруткой, ни обратиться к аудитории через кадр. Три вещи, нужные постоянно
 * (docs/DECISIONS.md, [2026-09-19] о канале команд ведущего).
 *
 * КНОПКА НЕОБЪЯВЛЕННОЙ КОМАНДЫ НЕДОСТУПНА С ПРИЧИНОЙ. Молчаливо серая кнопка
 * в эфире - это догадка: стример жмёт её, ничего не происходит, и он не знает,
 * сломалось ли что-то. Причина написана словами и читается чтецом.
 */
export function SessionControls() {
  const session = useConsoleSession();
  const command = useDirectorCommand();
  const message = useDirectorMessage();
  const toast = useToast();

  const [paused, setPaused] = useState(false);
  const [messageOpen, setMessageOpen] = useState(false);
  const [text, setText] = useState('');
  const [seconds, setSeconds] = useState<number>(DIRECTOR_MESSAGE_SECONDS.default);

  const accepts = (name: DirectorCommandName) => session.commands.includes(name);

  const run = (name: DirectorCommandName, done?: () => void) => {
    command.mutate(name, {
      onSuccess: () => {
        done?.();
      },
      onError: (error) => {
        // Ни одна мутация не завершается тихо: причина отказа - словами.
        const reason =
          typeof error === 'object' && error !== null && 'reason' in error
            ? String((error as { reason: unknown }).reason)
            : 'Команда не прошла. Попробуйте ещё раз.';
        toast.show({ tone: 'warning', title: 'Команда не выполнена', text: reason });
      },
    });
  };

  /** Подпись недоступной кнопки: почему её нельзя нажать именно сейчас. */
  const why = (name: DirectorCommandName) =>
    accepts(name) ? undefined : 'Эта игра не принимает такую команду';

  return (
    <>
      <div className="space-y-2 p-3" data-left="session-controls">
        {/*
          КЛАВИША - В ПОДСКАЗКЕ И ИМЕНИ ДЛЯ ЧТЕЦА, А НЕ В ПОДПИСИ (N-09).
          Единица 19 вписала её в видимую подпись, и ряд перестал быть
          однородным: «Пауза (клавиша P)» рядом с «Перезапустить раунд».
          У «Стопа» в шапке клавиша с самого начала жила в подсказке -
          теперь так у всех органов с клавишей, и это сторожат ворота
          (`e2e/console-hotkeys.spec.ts`, «клавиша не в подписи»).

          «Продолжить игру» - НЕ ГЛАВНАЯ: главная кнопка консоли одна и
          снимает блокировку эфира (решение владельца 2026-09-29, блок 2
          единицы 21), а пауза - ход игры, а не блокировка.
        */}
        <ControlButton
          id="pause"
          icon={paused ? 'play' : 'stop'}
          title={paused ? 'Продолжить игру' : 'Пауза'}
          hotkey={HOTKEYS.pause}
          reason={why(paused ? 'resume' : 'pause')}
          onClick={() => {
            run(paused ? 'resume' : 'pause', () => {
              setPaused(!paused);
            });
          }}
        />

        <ControlButton
          id="restart"
          icon="arrowReset"
          title="Перезапустить раунд"
          reason={why('restartRound')}
          onClick={() => {
            run('restartRound', () => {
              toast.show({ tone: 'success', title: 'Раунд перезапущен' });
            });
          }}
        />

        <ControlButton
          id="message"
          icon="chat"
          title="Сообщение зрителям"
          onClick={() => {
            setMessageOpen(true);
          }}
        />

        {/*
          РАЗРУШАЮЩЕЕ ДЕЙСТВИЕ - ДРУГОГО ВИДА, ЧЕМ СОСЕДИ (N-10). Промах мимо
          «Паузы» в эфире заканчивал сессию: одна высота, один вид, соседние
          строки. Теперь - сплошная красная рамка и Alert Dialog, который не
          закрывается промахом мимо.

          Имя пометки - словарём реестра (`console.left.ctrl-end-session`):
          орган ломает эфир, и ворота честности реестра требуют, чтобы он был
          назван сценарием ПОИМЁННО, а не в составе зоны.
        */}
        <Destructive
          id="end-session"
          solid
          className="w-full justify-start gap-2"
          data={{ 'data-left': 'ctrl-end-session' }}
          icon={<Icon name="signOut" size="sm" />}
          label="Завершить сессию"
          title="Завершить сессию?"
          confirm="Завершить сессию"
          onConfirm={() => {
            session.end();
          }}
        >
          Игра остановится и уйдёт с оверлея, консоль вернётся к каталогу. Настройки игры и привязки
          сохранятся.
        </Destructive>
      </div>

      <Modal
        open={messageOpen}
        onClose={() => {
          setMessageOpen(false);
        }}
        title="Сообщение зрителям"
        // Набранный текст - несохранённая правка: промах мимо окна не стирает её.
        dirty={text.trim() !== ''}
        actions={
          <>
            <Button
              variant="secondary"
              onClick={() => {
                setMessageOpen(false);
              }}
            >
              Отмена
            </Button>
            <Button
              variant="primary"
              data-left="shoutout-send"
              disabled={text.trim() === ''}
              aria-describedby={text.trim() === '' ? 'shoutout-why' : undefined}
              onClick={() => {
                message.mutate(
                  { text: text.trim(), seconds },
                  {
                    onSuccess: () => {
                      setMessageOpen(false);
                      setText('');
                      toast.show({ tone: 'success', title: 'Сообщение на оверлее' });
                    },
                    onError: (error) => {
                      const reason =
                        typeof error === 'object' && error !== null && 'reason' in error
                          ? String((error as { reason: unknown }).reason)
                          : 'Сообщение не ушло. Попробуйте ещё раз.';
                      toast.show({ tone: 'warning', title: 'Не отправлено', text: reason });
                    },
                  },
                );
              }}
            >
              Показать на оверлее
            </Button>
          </>
        }
      >
        <label className="text-foreground/80 text-xs font-medium" htmlFor="director-text">
          Текст
        </label>
        <Textarea
          id="director-text"
          data-left="shoutout-text"
          className="mt-1 min-h-20"
          maxLength={200}
          placeholder="Например: сейчас вернусь, не уходите"
          value={text}
          onChange={(event) => {
            setText(event.target.value);
          }}
        />

        {text.trim() === '' && (
          <Reason id="shoutout-why" className="mt-1">
            Напишите текст — пустое сообщение показывать нечего.
          </Reason>
        )}

        <p className="text-foreground/80 mt-3 text-sm font-medium">Сколько держать на оверлее</p>
        <Choice
          label="Сколько держать на оверлее"
          value={String(seconds)}
          onChange={(next) => {
            setSeconds(Number(next));
          }}
          size="sm"
          className="mt-1"
          data-left="shoutout-duration"
          options={[5, 10, 15, 30, 60].map((value) => ({
            value: String(value),
            label: `${value} с`,
          }))}
        />
      </Modal>
    </>
  );
}

/**
 * Кнопка управления. Недоступность ВСЕГДА объяснена: подпись под кнопкой видна
 * глазом и прочитана чтецом через `aria-describedby`.
 */
function ControlButton({
  id,
  icon,
  title,
  hotkey,
  reason,
  onClick,
}: {
  id: string;
  icon: 'play' | 'stop' | 'arrowReset' | 'chat';
  /* Все органы хода игры - одного вида `secondary`: главная кнопка
     консоли одна, и это не они. Прежняя редакция передавала сюда вид
     строкой, и четыре кнопки одного ряда были четырёх разных видов. */
  title: string;
  /** Горячая клавиша: в подсказку и в имя для чтеца, не в подпись. */
  hotkey?: string;
  reason?: string;
  onClick: () => void;
}) {
  const hintId = `control-${id}-why`;

  return (
    <div>
      <Button
        data-left={`ctrl-${id}`}
        variant="secondary"
        size="s"
        className="w-full justify-start gap-2"
        disabled={reason !== undefined}
        aria-describedby={reason === undefined ? undefined : hintId}
        aria-label={hotkey === undefined ? undefined : `${title}, клавиша ${hotkey}`}
        aria-keyshortcuts={hotkey}
        title={hotkey === undefined ? undefined : `${title} (клавиша ${hotkey})`}
        onClick={onClick}
      >
        <Icon name={icon} size="sm" />
        {title}
      </Button>
      {reason !== undefined && (
        <Reason id={hintId} className="mt-1">
          {reason}
        </Reason>
      )}
    </div>
  );
}
